<template>
  <LogTimeReportFormModal v-model="show" :title="titleText" id="log-time-form-modal" :loading="isLoading">
    <FormGenerator
      ref="log-time-form"
      class="form-generator-container"
      :data="form.fields"
      :textButtonSubmit="submitButtonText"
      :showReset="false"
      @submit="submit"
      @input="changeFormFields($event)"
    >
      <template v-slot:project="slotProps">
        <b-form-group>
          <label class="form__label">{{ slotProps.data.label }}</label>
          <div v-b-tooltip.hover="{ variant: 'base', placement: 'top', placement:'top', boundary:'viewport' }" :title="slotProps.data.value ? slotProps.data.value[slotProps.data.selectLabel] : null">
            <multiselect
              v-model="slotProps.data.value"
              :options="slotProps.data.options"
              :track-by="slotProps.data.valueField"
              :label="slotProps.data.selectLabel"
              :showLabels="false"
              :class="{ 'multiselect__single-line': true }"
              :name="slotProps.data.name"
              allow-empty
              @select="changeProject"
            />
          </div>
        </b-form-group>
      </template>
      <template v-slot:task="slotProps">
        <b-form-group>
          <label class="form__label">{{ slotProps.data.label }}</label>
          <div v-b-tooltip.hover="{ variant: 'base', placement: 'top', placement:'top', boundary:'viewport' }" :title="slotProps.data.value ? slotProps.data.value[slotProps.data.selectLabel] : null">
            <multiselect
              v-model="slotProps.data.value"
              :options="slotProps.data.options"
              :track-by="slotProps.data.valueField"
              :label="slotProps.data.selectLabel"
              :name="slotProps.data.name"
              :showLabels="false"
              :class="{ 'multiselect__single-line': true }"
              :placeholder="isFetchingTasks ? 'Loading...' : 'Select task'"
              :disabled="isFetchingTasks"
              allow-empty
            />
          </div>
        </b-form-group>
      </template>
      <template v-slot:user="slotProps">
        <b-form-group>
          <label class="form__label">{{ slotProps.data.label }}</label>
          <multiselect
            v-model="slotProps.data.value"
            :options="slotProps.data.options"
            :track-by="slotProps.data.valueField"
            :label="slotProps.data.selectLabel"
            :name="slotProps.data.name"
            :disabled="true"
            :showLabels="false"
            allow-empty
          />
        </b-form-group>
      </template>
      <template v-slot:time="slotProps">
        <b-form-group>
          <label class="form__label">{{ slotProps.data.label }}</label>
          <b-form-input
            type="text"
            :name="slotProps.data.name"
            v-model="slotProps.data.value"
          />
        </b-form-group>
      </template>
      <template v-slot:logged_at="slotProps">
        <b-form-group>
          <label class="form__label">{{ slotProps.data.label }}</label>
          <div class="row">
            <div class="col-6">
              <Datepicker
                v-model="loggedAtDate"
                :bootstrap-styling="true"
                :format="slotProps.data.format"
                :placeholder="slotProps.data.placeholder"
                :name="slotProps.data.name+'_date'"
                :use-utc="true"
              />
            </div>
            <div class="col-6">
              <b-form-input v-model="loggedAtTime" :name="slotProps.data.name+'_time'" type="time"></b-form-input>
            </div>
          </div>
        </b-form-group>
      </template>
      <template v-slot:comment="slotProps">
        <b-form-group>
          <label>{{ slotProps.data.label }}</label>
          <b-form-textarea
            id="textarea"
            v-model="slotProps.data.value"
            :name="slotProps.data.name"
            rows="3"
            max-rows="6"
          >
          </b-form-textarea>
        </b-form-group>
      </template>
      <template v-slot:LoginToJira>
        <LogTimeLoginToJira v-if="isProjectConnectedToJira && !isJiraAuth">
          <template #text>
            To log time to this task you need to log in to Jira
          </template>
        </LogTimeLoginToJira>
      </template>
    </FormGenerator>
  </LogTimeReportFormModal>
</template>

<script>
import { cloneDeep } from 'lodash'
import { mapActions, mapGetters } from 'vuex'
import Moment from 'moment/moment'
import Datepicker from 'vuejs-datepicker'
import FormGenerator from '@components/FormGenerator'
import LogTimeReportFormModal from '@components/log-time-report/LogTimeReportFormModal.vue'
import LogTimeLoginToJira from '../log-time-report/LogTimeLoginToJira.vue'
import Global from '@/mixins/Global'

export default {
  name: 'LogTimeFormModal',
  mixins: [Global],
  components: {
    FormGenerator,
    Datepicker,
    LogTimeReportFormModal,
    LogTimeLoginToJira
  },
  props: {
    isLoading: {
      type: Boolean,
      default: false
    },
    mode: {
      type: String,
      default: 'create'
    },
    dataProps: {
      type: Object
    },
    value: {
      type: Boolean
    }
  },
  data () {
    const defaultForm = {
      fields: {
        'id': {
          name: 'id',
          value: null,
          hidden: true
        },
        'project': {
          type: 'slot',
          slotName: 'project',
          name: 'project_id',
          label: 'Project',
          selectLabel: 'label',
          valueField: 'id',
          options: [],
          value: null,
          class: 'col-6 field-container'
        },
        'task': {
          type: 'slot',
          slotName: 'task',
          name: 'task_id',
          label: 'Task',
          selectLabel: 'label',
          valueField: 'id',
          options: [],
          value: null,
          class: 'col-6 field-container'
        },
        'user': {
          type: 'slot',
          slotName: 'user',
          name: 'user_id',
          label: 'User',
          selectLabel: 'name',
          valueField: 'id',
          options: [],
          value: null,
          class: 'col-12 field-container'
        },
        'time': {
          type: 'slot',
          slotName: 'time',
          name: 'time',
          label: 'Time (Format: 1d 1h 30m)',
          placeholder: '0d 0h 0m',
          value: '',
          class: 'col-6 field-container'
        },
        'logged_at': {
          type: 'slot',
          slotName: 'logged_at',
          name: 'logged_at',
          label: 'Started at',
          format: 'yyyy-MM-dd',
          placeholder: 'Date and Time',
          class: 'col-6 field-container',
          defaultValue: Moment().format('YYYY-MM-DD HH:mm'),
          value: null
        },
        'comment': {
          type: 'slot',
          slotName: 'comment',
          name: 'comment',
          label: 'Comment',
          placeholder: 'Comment',
          value: '',
          class: 'col-12 field-container'
        },
        'login_to_jira': {
          type: 'slot',
          slotName: 'LoginToJira',
          class: 'col-12'
        }
      }
    }

    return {
      defaultForm,
      form: cloneDeep(defaultForm),
      tasks: [],
      isFetchingTasks: false
    }
  },
  computed: {
    ...mapGetters({
      getUser: 'getUser',
      getProjects: 'projects/getProjects',
      getTasks: 'tasks/getTasks',
      getAllUsers: 'getAllUsersWithTrashed'
    }),
    show: {
      get () {
        return this.value
      },
      set (val) {
        this.$emit('input', val)
      }
    },
    isProjectConnectedToJira () {
      const project = this.form.fields.project?.value
      const isJiraProject = project && project.is_custom_project !== 1
      return isJiraProject
    },
    loggedAtDate: {
      get () {
        if (this.form.fields.logged_at.value) {
          return Moment(this.form.fields.logged_at.value).format('YYYY-MM-DD')
        }
        return Moment().format('YYYY-MM-DD')
      },
      set (val) {
        const [hours, minutes] = this.loggedAtTime.split(':')
        this.form.fields.logged_at.value = Moment(val)
          .utc()
          .set('hour', hours)
          .set('minute', minutes)
          .format('YYYY-MM-DD HH:mm')
      }
    },
    loggedAtTime: {
      get () {
        if (this.form.fields.logged_at.value) {
          return Moment(this.form.fields.logged_at.value).format('HH:mm')
        }
        return Moment().format('HH:mm')
      },
      set (val) {
        const [hours, minutes] = val.split(':')
        this.form.fields.logged_at.value = Moment(this.loggedAtDate)
          .set('hours', hours)
          .set('minutes', minutes)
          .format('YYYY-MM-DD HH:mm')
      }
    },
    submitButtonText () {
      return this.mode === 'edit' ? 'Save' : 'Create'
    },
    titleText () {
      return this.mode === 'edit' ? 'Edit log time' : 'Add log time'
    }
  },
  watch: {
    'value': {
      deep: true,
      handler () {
        if (this.value && !this.dataProps) {
          this.resetForm()
        }
      }
    }
  },
  async mounted () {
    await this.setInitialForm()
    if (this.dataProps) {
      this.form.fields.id.value = this.dataProps.id ?? this.form.fields.id.value
      this.form.fields.time.value = this.dataProps.time_formatted ?? this.form.fields.time.value
      this.form.fields.logged_at.value = this.dataProps.logged_at ?? this.form.fields.logged_at.value
      this.form.fields.comment.value = this.dataProps.comment ?? this.form.fields.comment.value
      this.form.fields.user.value = this.dataProps.user ?? this.form.fields.user.value
      if (this.dataProps.task) {
        if (this.dataProps.task.project) {
          await this.changeProject(this.dataProps.task.project)
        }
        this.form.fields.task.value = this.dataProps.task
      }
    }
  },
  methods: {
    ...mapActions({
      setIsModalLoading: 'finances/setIsModalLoading'
    }),
    resetForm () {
      this.form = cloneDeep(this.defaultForm)
      this.setInitialForm()
    },
    async setInitialForm () {
      if (!this.getProjects.length) {
        await this.$api.projects.getProjects()
      }
      if (!this.getAllUsers.length) {
        await this.$api.users.getAllUsers()
      }
      this.form.fields.id.value = 0
      this.form.fields.project.options = this.getProjects
      this.form.fields.task.options = []
      this.form.fields.user.options = this.getAllUsers
      this.form.fields.user.value = this.getAllUsers.find(user => user.id === this.getUser.id)
      this.form.fields.time.value = ''
      this.form.fields.logged_at.value = this.mode === 'edit' ? '' : this.form.fields.logged_at.defaultValue
      this.form.fields.comment.value = ''
    },
    async submit (data) {
      this.$emit('submit', data)
    },
    hideModal () {
      this.setIsModalLoading(false)
    },
    changeFormFields (event) {
      this.form.fields = event
    },
    async changeProject (project) {
      this.form.fields.project.value = project
      this.form.fields.task.options = []
      this.form.fields.task.value = null
      this.isFetchingTasks = true
      await this.$api.tasks.getTasks({ project_ids: [project.id], labels_only: true })
      this.isFetchingTasks = false
      this.form.fields.task.options = this.getTasks
      if (this.form.fields.task.options.length) {
        this.form.fields.task.value = this.form.fields.task.options[0]
      } else {
        this.form.fields.task.value = null
      }
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep .field-container {
  margin-bottom: 4px;
}

.commission-column {
  max-width: 200px;
}
.total-amount {
  float:right;
  font-size: x-large;
}
.fetch-tasks-spinner {
  position:absolute;
  top:40px;
  left:15px;
  z-index:1;
}
</style>
